import { Inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Environment } from '../models/environment.model';

const PROFILE_IMAGE_PROXY_PATH = '/public/profile-images/';
const LEGACY_HOST_SUFFIX = '.jowaapp.com';
const FALLBACK_JOWA_BASE_URL = 'https://api.jowaapp.com';

const normalizeString = (value: unknown): string => `${value || ''}`.trim();

const getProfileImageBaseUrl = (apiBaseUrl: string): string => {
  try {
    return `${new URL(apiBaseUrl).origin}${PROFILE_IMAGE_PROXY_PATH}`;
  } catch (error) {
    return `${FALLBACK_JOWA_BASE_URL}${PROFILE_IMAGE_PROXY_PATH}`;
  }
};

const normalizeProfileImageUrl = (rawValue: string, profileImageBaseUrl: string): string => {
  const normalized = normalizeString(rawValue);
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    return normalized;
  }

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.toLowerCase();
    if (!host.endsWith(LEGACY_HOST_SUFFIX) || !parsed.pathname.startsWith(PROFILE_IMAGE_PROXY_PATH)) {
      return normalized;
    }

    const encodedImageKey = parsed.pathname.slice(PROFILE_IMAGE_PROXY_PATH.length);
    return `${profileImageBaseUrl}${encodedImageKey}${parsed.search}${parsed.hash}`;
  } catch (error) {
    return normalized;
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const normalizeBody = (value: unknown, profileImageBaseUrl: string): unknown => {
  if (typeof value === 'string') {
    return normalizeProfileImageUrl(value, profileImageBaseUrl);
  }

  if (Array.isArray(value)) {
    let hasChanges = false;
    const normalizedArray = value.map((item) => {
      const normalizedItem = normalizeBody(item, profileImageBaseUrl);
      if (normalizedItem !== item) {
        hasChanges = true;
      }

      return normalizedItem;
    });

    return hasChanges ? normalizedArray : value;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  let hasChanges = false;
  const normalizedObject: Record<string, unknown> = {};

  Object.keys(value).forEach((key) => {
    const currentValue = value[key];
    const normalizedValue = normalizeBody(currentValue, profileImageBaseUrl);
    normalizedObject[key] = normalizedValue;

    if (normalizedValue !== currentValue) {
      hasChanges = true;
    }
  });

  return hasChanges ? normalizedObject : value;
};

@Injectable()
export class ProfileImageHostInterceptor implements HttpInterceptor {
  constructor(@Inject('environment') private environment: Environment) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const profileImageBaseUrl = getProfileImageBaseUrl(this.environment.api.url);

    return next.handle(request).pipe(
      map((event: HttpEvent<unknown>) => {
        if (!(event instanceof HttpResponse) || event.body === null || event.body === undefined) {
          return event;
        }

        const normalizedBody = normalizeBody(event.body, profileImageBaseUrl);
        if (normalizedBody === event.body) {
          return event;
        }

        return event.clone({ body: normalizedBody });
      })
    );
  }
}
